import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";

export class LeaveLevelDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 909;
  private static readonly BG_Y = 33;
  private static readonly BG_H = 1245;
  private static readonly DESIGN_H = LeaveLevelDialog.BG_Y + LeaveLevelDialog.BG_H;
  private static readonly FRAME_W = 760;
  private static readonly FRAME_H = 334;
  private static readonly FRAME_Y = LeaveLevelDialog.BG_Y + 250;

  private readonly on_exit?: () => void;

  constructor( on_exit?: () => void )
  {
    const body_w = LayoutScaler.widthFromDesign( LeaveLevelDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( LeaveLevelDialog.DESIGN_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.on_exit = on_exit;

    this.createBackground();
    this.createFrame();
    this.createTitle();
    this.createText();
    this.createCloseButton();
    this.createActionButton();
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( LeaveLevelDialog.BG_Y ),
      LayoutScaler.widthFromDesign( LeaveLevelDialog.DESIGN_W ),
      LayoutScaler.heightFromDesign( LeaveLevelDialog.BG_H ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );
  }

  private createFrame(): void
  {
    const design_frame_x = ( LeaveLevelDialog.DESIGN_W - LeaveLevelDialog.FRAME_W ) / 2;

    const frame_x = LayoutScaler.xFromDesign( design_frame_x );
    const frame_y = LayoutScaler.yFromDesign( LeaveLevelDialog.FRAME_Y );
    const frame_w = LayoutScaler.widthFromDesign( LeaveLevelDialog.FRAME_W );
    const frame_h = LayoutScaler.heightFromDesign( LeaveLevelDialog.FRAME_H );

    const frame = new UIImage( this.ctx, frame_x, frame_y, frame_w, frame_h, ImgID.UI_BoosterPaternedBg );
    this.addChild( frame );

    const design_heart_size = 340;
    const design_heart_x = ( LeaveLevelDialog.DESIGN_W - design_heart_size ) / 2;
    const design_heart_y = LeaveLevelDialog.FRAME_Y + ( LeaveLevelDialog.FRAME_H - design_heart_size ) / 2;

    const heart_x = LayoutScaler.xFromDesign( design_heart_x );
    const heart_y = LayoutScaler.yFromDesign( design_heart_y );
    const heart_w = LayoutScaler.widthFromDesign( design_heart_size );
    const heart_h = LayoutScaler.heightFromDesign( design_heart_size );

    const heart = new UIImage( this.ctx, heart_x, heart_y, heart_w, heart_h, ImgID.UI_CrushedHeart );
    this.addChild( heart );
  }

  private createTitle(): void
  {
    const design_title_y = LeaveLevelDialog.BG_Y + 99;
    const design_title_h = 99;
    const design_font_size = 70;
    const design_outline_w = 12;

    const title_full_w = LayoutScaler.widthFromDesign( LeaveLevelDialog.DESIGN_W );
    const MAGIC_NUM_CORRECTION = 0.8;
    const title_w = title_full_w * MAGIC_NUM_CORRECTION;
    const title_x = ( title_full_w - title_w ) / 2;
    const title_y = LayoutScaler.yFromDesign( design_title_y );
    const title_h = LayoutScaler.heightFromDesign( design_title_h );

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "LEAVE_LEVEL.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    this.addChild( title );
  }

  private createText(): void
  {
    const design_text_y = LeaveLevelDialog.FRAME_Y + LeaveLevelDialog.FRAME_H + 65;
    const design_text_h = 200;
    const design_font_size = 60;

    const full_w = LayoutScaler.widthFromDesign( LeaveLevelDialog.DESIGN_W );
    const MAGIC_NUM_CORRECTION = 0.8;
    const text_w = full_w * MAGIC_NUM_CORRECTION;
    const text_x = ( full_w - text_w ) / 2;
    const text_y = LayoutScaler.yFromDesign( design_text_y );
    const text_h = LayoutScaler.heightFromDesign( design_text_h );

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        localization_key: "CLOSE_LEVEL_DIALOG.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: ColorScheme.leave_level_text_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        auto_wrap: true,
      }
    );
    this.addChild( text );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 114;
    const design_btn_x = LeaveLevelDialog.DESIGN_W - 80;
    const design_btn_y = 0;

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      LayoutScaler.widthFromDesign( design_btn_size ),
      LayoutScaler.heightFromDesign( design_btn_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => DialogManager.closeDialog() );
    this.addChild( btn );
  }

  private createActionButton(): void
  {
    const design_btn_w = 700;
    const design_btn_h = 280;
    const design_btn_y = LeaveLevelDialog.DESIGN_H - design_btn_h - 65;
    const design_btn_x = ( LeaveLevelDialog.DESIGN_W - design_btn_w ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      btn_w,
      btn_h,
      ImgID.UI_RedBtn,
      undefined,
      true
    );

    const design_font_size = 60;

    const text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "CLOSE_GAME_DIALOG.EXIT_BUTTON",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
        outline_color: ColorScheme.red_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );
    btn.addChild( text, true );

    btn.setOnClick( () =>
    {
      DialogManager.closeDialog();
      this.on_exit?.();
    } );
    this.addChild( btn );
  }

  public name(): string
  {
    return "LeaveLevelDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
